import { Link, NavLink, Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Instagram, Facebook, Youtube, Menu } from "lucide-react";
import TikTokIcon from "@/components/icons/TikTokIcon";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import Logo from "@/assets/Logo.png";
import Impressum from "@/assets/Impressum.pdf";
import Datenschutz from "@/assets/Datenschutz.pdf";

export default function PublicLayout() {
  const { t } = useTranslation();
  const [social, setSocial] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    api.get("/public/social").then((r) => setSocial(r.data)).catch(() => { });
  }, []);

  const navCls = ({ isActive }) =>
    `text-sm font-medium tracking-wide transition-colors hover:text-bordeaux ${isActive ? "text-bordeaux" : "text-slate-600"
    }`;

  const socials = [
    { key: "instagram", url: social.instagram_url, icon: <Instagram size={16} />, label: "Instagram" },
    { key: "tiktok", url: social.tiktok_url, icon: <TikTokIcon size={15} />, label: "TikTok" },
    { key: "facebook", url: social.facebook_url, icon: <Facebook size={16} />, label: "Facebook" },
    { key: "youtube", url: social.youtube_url, icon: <Youtube size={16} />, label: "YouTube" },
  ].filter((s) => s.url && s.url.trim());

  return (
    <div className="min-h-screen bg-beige text-slate-900 flex flex-col">
      <header
        data-testid="public-header"
        className="bg-beige/80 backdrop-blur-xl border-b border-slate-200/60 sticky top-0 z-50"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" data-testid="public-logo" className="flex items-center gap-3 group">
            <span className="w-12 h-12 text-beige-light grid place-items-center font-bold rounded-sm"><img src={Logo} alt="Refah Europa e.V." className="w-full h-full object-contain" /></span>
            <span className="font-semibold tracking-tight text-slate-900">
              Refah <span className="text-bordeaux-accent">Europa e.V.</span>
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <NavLink to="/" end className={navCls} data-testid="nav-home">{t("nav.home")}</NavLink>
            <NavLink to="/news" className={navCls} data-testid="nav-news">{t("nav.news")}</NavLink>
            <NavLink to="/mitglieder" className={navCls} data-testid="nav-members">{t("nav.members")}</NavLink>
            <NavLink to="/galerie" className={navCls} data-testid="nav-gallery">{t("nav.gallery")}</NavLink>
            <NavLink to="/spenden" className={navCls} data-testid="nav-donate">{t("nav.donate")}</NavLink>
            <NavLink to="/kontakt" className={navCls} data-testid="nav-contact">{t("nav.contact")}</NavLink>
          </nav>
          <div className="hidden md:flex items-center gap-6">
            <LanguageSwitcher />
            <Link
              to="/admin/login"
              data-testid="nav-admin-login"
              className="text-xs tracking-widest uppercase text-slate-500 hover:text-bordeaux transition-colors"
            >
              {t("nav.admin")}
            </Link>
          </div>

          {/* Mobile menu trigger */}
          <div className="md:hidden flex items-center gap-3">
            <LanguageSwitcher />
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  data-testid="mobile-menu-btn"
                  aria-label={t("nav.openMenu")}
                  className="w-10 h-10 grid place-items-center border border-slate-200 hover:border-bordeaux hover:bg-bordeaux hover:text-beige-light text-slate-700 rounded-sm transition-colors"
                >
                  <Menu size={18} />
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                data-testid="mobile-menu-panel"
                className="w-[85%] sm:w-[360px] bg-beige border-l border-bordeaux/20 p-0 flex flex-col"
              >
                <SheetHeader className="px-6 py-5 border-b border-slate-200 text-left">
                  <SheetTitle className="flex items-center gap-3 text-slate-900">
                    <span className="w-12 h-12 text-beige-light grid place-items-center font-bold rounded-sm"><img src={Logo} alt="Refah Europa e.V." className="w-full h-full object-contain" /></span>
                    <span className="font-semibold tracking-tight">
                      Refah <span className="text-bordeaux-accent">Europa e.V.</span>
                    </span>
                  </SheetTitle>
                </SheetHeader>

                <nav className="flex-1 flex flex-col gap-1 overflow-y-auto px-2 py-4 thin-scroll">
                  {[
                    { to: "/", label: t("nav.home"), end: true, testId: "mobile-nav-home" },
                    { to: "/news", label: t("nav.news"), testId: "mobile-nav-news" },
                    { to: "/mitglieder", label: t("nav.members"), testId: "mobile-nav-members" },
                    { to: "/galerie", label: t("nav.gallery"), testId: "mobile-nav-gallery" },
                    { to: "/spenden", label: t("nav.donate"), testId: "mobile-nav-donate" },
                    { to: "/kontakt", label: t("nav.contact"), testId: "mobile-nav-contact" },
                  ].map((item) => (
                    <SheetClose asChild key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.end}
                        data-testid={item.testId}
                        className={({ isActive }) =>
                          `block px-4 py-3.5 text-base font-medium rounded-sm transition-colors ${isActive
                            ? "bg-bordeaux text-beige-light"
                            : "text-slate-700 hover:bg-bordeaux/10 hover:text-bordeaux"
                          }`
                        }
                      >
                        {item.label}
                      </NavLink>
                    </SheetClose>
                  ))}
                </nav>

                <div className="border-t border-slate-200 px-6 py-5 space-y-4 bg-beige-light">
                  <SheetClose asChild>
                    <Link
                      to="/admin/login"
                      data-testid="mobile-nav-admin"
                      className="block text-xs uppercase tracking-[0.3em] text-slate-500 hover:text-bordeaux text-center"
                    >
                      {t("nav.admin")}
                    </Link>
                  </SheetClose>
                  {socials.length > 0 && (
                    <div className="flex items-center justify-center gap-2 pt-1">
                      {socials.map((s) => (
                        <a
                          key={s.key}
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={s.label}
                          data-testid={`mobile-social-${s.key}`}
                          className="w-10 h-10 grid place-items-center border border-slate-200 hover:border-bordeaux hover:bg-bordeaux hover:text-beige-light text-slate-600 rounded-sm transition-colors"
                        >
                          {s.icon}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-bordeaux/15 bg-beige-light">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid md:grid-cols-3 gap-8 text-sm text-slate-600">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-9 h-9 text-beige-light grid place-items-center font-bold rounded-sm"><img src={Logo} alt="Refah Europa e.V." className="w-full h-full object-contain" /></span>
              <span className="font-semibold text-slate-900">Refah Europa e.V.</span>
            </div>
            <p className="text-slate-500 leading-relaxed">{t("footer.tagline")}</p>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-widest text-slate-500 mb-3">{t("footer.content")}</h4>
            <ul className="space-y-2">
              <li><Link to="/news" className="hover:text-bordeaux">{t("nav.news")}</Link></li>
              <li><Link to="/mitglieder" className="hover:text-bordeaux">{t("nav.members")}</Link></li>
              <li><Link to="/galerie" className="hover:text-bordeaux">{t("nav.gallery")}</Link></li>
              <li><Link to="/spenden" className="hover:text-bordeaux">{t("nav.donate")}</Link></li>
              <li><Link to="/kontakt" className="hover:text-bordeaux">{t("nav.contact")}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-widest text-slate-500 mb-3">{t("footer.admin")}</h4>
            <ul className="space-y-2">
              <li><Link to="/admin/login" className="hover:text-bordeaux">{t("footer.adminLogin")}</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-bordeaux/15">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <span className="text-xs text-slate-500">© {new Date().getFullYear()} Refah Europa e.V. {t("footer.rights")}</span>
            <span className="text-xs text-slate-500">
              <a
                href={Impressum}
                target="_blank"
                rel="noopener noreferrer">
                Impressum
              </a>
            </span>
            <span className="text-xs text-slate-500">
              <a
                href={Datenschutz}
                target="_blank"
                rel="noopener noreferrer">
                Datenschutz
              </a>
            </span>
            {socials.length > 0 && (
              <div data-testid="social-links" className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mr-2 hidden sm:inline">
                  {t("footer.followUs")}
                </span>
                {socials.map((s) => (
                  <a
                    key={s.key}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    data-testid={`social-link-${s.key}`}
                    className="w-9 h-9 grid place-items-center border border-slate-200 hover:border-bordeaux hover:bg-bordeaux hover:text-beige-light text-slate-600 rounded-sm transition-colors"
                  >
                    {s.icon}
                  </a>
                ))}
              </div>
            )}

            <span className="text-xs tracking-widest uppercase text-slate-500">v1.0</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
